import { Form, type FormikProps } from "formik";
import React, { useState } from "react";
import * as yup from "yup";

import { Button2 } from "@/components/UI2/atoms/Button";
import { CustomForm } from "@/component-library/CustomForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { EMERGENCY_SCHEMA } from "@/containers/Dashboard/Patient/schema/PatientProfileInfoSchema";
import FormikInput from "@/components/UI/FormikInput";
import FormikMobileInput from "@/components/UI/formikMobileInput";
import Hr from "@/components/UI/Hr";
import type { PatientProfile } from "@/core/types";
import { RE_PHONE } from "@/constants/regex";
import { renderTitleWithValue } from "@/components/Patient/Profile/renderTitleWithValue";

import type { UpdatePatientProfileVariables } from "../api/queryFunctions/updatePatientProfile";

/** Only mobile number format needs to be validated, no required fields */
const VALIDATION_SCHEMA = yup.object().shape({
  emergency_contact_number: yup
    .string()
    .matches(RE_PHONE, "Emergency Contact Number must be a valid US number"),
});

type Props = {
  data: PatientProfile;
  submit: (
    data: UpdatePatientProfileVariables,
    actions: { setSubmitting: (isSubmitting: boolean) => void },
  ) => void;
};

const readItems = (data: PatientProfile) => [
  { title: "Emergency Contact Person", value: data.emergency_contact_person || "" },
  { title: "Emergency Contact Number", value: data.emergency_contact_number || "" },
];

export function EmergencyContactSection({ data, submit }: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = (
    formData: UpdatePatientProfileVariables,
    actions: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    setIsEditing(false);
    submit(formData, actions);
  };

  const renderForm = (formikProps: FormikProps<UpdatePatientProfileVariables>) => (
    <Form style={{ width: "100%" }}>
      <div className="mx-auto px-0">
        <div className="container px-0">
          <div className="row">
            {EMERGENCY_SCHEMA.map((formEl) => (
              <div className="col-12 col-lg-6" key={formEl.name}>
                {formEl.name === "emergency_contact_number" ? (
                  <FormikMobileInput
                    errors={formikProps.errors}
                    formEl={formEl}
                    onChange={async (val: { value: string }) => {
                      await formikProps.setFieldValue("emergency_contact_number", val.value);
                      formikProps.setFieldTouched("emergency_contact_number");
                    }}
                    touched={formikProps.touched}
                    value={formikProps.values.emergency_contact_number}
                  />
                ) : (
                  <FormikInput
                    errors={formikProps.errors}
                    formEl={formEl}
                    touched={formikProps.touched}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-3 d-lg-none">
        <Hr className="" />
      </div>

      <div className="tw:flex tw:justify-end tw:gap-3">
        <Button2 onPress={() => setIsEditing(false)} size="md" type="button" variant="secondary">
          Cancel
        </Button2>
        <Button2
          isDisabled={formikProps.isSubmitting}
          isLoading={formikProps.isSubmitting}
          size="md"
          type="submit"
          variant="primary"
        >
          Save
        </Button2>
      </div>
    </Form>
  );

  return (
    <DashboardCard2
      Actions={
        <Button2 isDisabled={isEditing} onPress={() => setIsEditing(true)} variant="link">
          Edit
        </Button2>
      }
      title="Emergency Info"
    >
      <div className="PatientProfile__basic-info no-gutters row pt-2">
        {!isEditing &&
          readItems(data).map((item) => (
            <div className="col-12 col-lg-6" key={item.title}>
              {renderTitleWithValue(item.title, item.value)}
            </div>
          ))}

        {isEditing && (
          <CustomForm
            initialValues={data}
            onSubmit={handleSubmit}
            render={renderForm}
            validationSchema={VALIDATION_SCHEMA}
          />
        )}
      </div>
    </DashboardCard2>
  );
}
